import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { supabase } from '../../services/supabaseClient';
import './ArtistProfile.css';

/**
 * ArtistProfile component for displaying a single artist's full profile
 */
const ArtistProfile = () => {
  const { id } = useParams();
  const [artist, setArtist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchArtist = async () => {
      try {
        setLoading(true);

        // Fetch the artist profile with all details
        const { data, error } = await supabase
          .from('profiles')
          .select('*, gigs(*)')
          .eq('id', id)
          .single();
        
        if (error) {
          throw error;
        }
        
        if (data) {
          setArtist(data);
          console.log('Artist data:', data);
        }
      } catch (error) {
        console.error('Error fetching artist:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };
    
    if (id) {
      fetchArtist();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="artist-profile-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading artist profile...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="artist-profile-container">
        <div className="error-message">
          <h2>Error Loading Profile</h2>
          <p>{error}</p>
          <Link to="/artists" className="back-button">Back to Artists</Link>
        </div>
      </div>
    );
  }

  if (!artist) {
    return (
      <div className="artist-profile-container">
        <div className="error-message">
          <h2>Artist Not Found</h2>
          <p>The artist profile you're looking for doesn't exist or has been removed.</p>
          <Link to="/artists" className="back-button">Back to Artists</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="artist-profile-container">
      <div className="profile-header">
        <Link to="/artists" className="back-button">
          <i className="fas fa-arrow-left"></i> Back to Artists
        </Link>
        
        <div className="profile-hero">
          <div className="profile-image-container">
            {artist.profile_image_url ? (
              <img 
                src={artist.profile_image_url} 
                alt={artist.full_name} 
                className="profile-image"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = 'https://via.placeholder.com/150';
                }}
              />
            ) : (
              <div className="profile-image-placeholder">
                {artist.full_name ? artist.full_name.charAt(0).toUpperCase() : '?'}
              </div>
            )}
          </div>
          
          <div className="profile-title">
            <h1>{artist.full_name}</h1>
            <p className="profile-profession">
              {artist.profession || 
                (artist.professions && artist.professions.length > 0 
                  ? artist.professions[0] 
                  : artist.instruments && artist.instruments.length > 0 
                    ? artist.instruments[0] 
                    : 'Musician')}
            </p>
            
            {artist.location && (
              <p className="profile-location">
                <i className="fas fa-map-marker-alt"></i> {artist.location}
              </p>
            )}
          </div>
        </div>
      </div>
      
      <div className="profile-content">
        <div className="profile-section">
          <h2>About</h2>
          {artist.bio ? (
            <p className="profile-bio">{artist.bio}</p>
          ) : (
            <p className="profile-bio empty">No bio provided.</p>
          )}
        </div>
        
        <div className="profile-section profile-details">
          <h2>Details</h2>
          
          <div className="details-grid">
            {/* Professions */}
            {artist.professions && artist.professions.length > 0 && (
              <div className="detail-item">
                <h3>Profession(s)</h3>
                <p>{artist.professions.join(', ')}</p>
              </div>
            )}
            
            {/* Instruments */}
            {artist.instruments && artist.instruments.length > 0 && (
              <div className="detail-item">
                <h3>Instrument(s)</h3>
                <p>{artist.instruments.join(', ')}</p>
              </div>
            )}
            
            {/* Genres */}
            {artist.genres && artist.genres.length > 0 && (
              <div className="detail-item">
                <h3>Genre(s)</h3>
                <p>{artist.genres.join(', ')}</p>
              </div>
            )}
            
            {/* Experience Level */}
            {artist.experience_level && (
              <div className="detail-item">
                <h3>Experience Level</h3>
                <p>{artist.experience_level}</p>
              </div>
            )}
            
            {/* Years of Experience */}
            {artist.years_of_experience && (
              <div className="detail-item">
                <h3>Years of Experience</h3>
                <p>{artist.years_of_experience} years</p>
              </div>
            )}
            
            {/* Hourly Rate */}
            {artist.hourly_rate && (
              <div className="detail-item">
                <h3>Hourly Rate</h3>
                <p>${artist.hourly_rate}/hour</p>
              </div>
            )}
            
            {/* Payment Preference */}
            {artist.payment_preference && (
              <div className="detail-item">
                <h3>Payment Preference</h3>
                <p>{artist.payment_preference}</p>
              </div>
            )}
            
            {/* Contact Information */}
            {artist.email && (
              <div className="detail-item">
                <h3>Email</h3>
                <p>{artist.email}</p>
              </div>
            )}
            
            {artist.phone && (
              <div className="detail-item">
                <h3>Phone</h3>
                <p>{artist.phone}</p>
              </div>
            )}
            
            {/* Availability */}
            {artist.availability && artist.availability.length > 0 && (
              <div className="detail-item">
                <h3>Availability</h3>
                <p>{artist.availability.join(', ')}</p>
              </div>
            )}
            
            {/* Rating */}
            {artist.rating && (
              <div className="detail-item">
                <h3>Rating</h3>
                <div className="rating">
                  {'★'.repeat(Math.floor(artist.rating))}
                  {'☆'.repeat(5 - Math.floor(artist.rating))}
                </div>
              </div>
            )}
          </div>
        </div>
        
        {/* Contact section has been removed */}
        
        {/* Portfolio Section - if the artist has any portfolio items */}
        {artist.portfolio_items && artist.portfolio_items.length > 0 && (
          <div className="profile-section">
            <h2>Portfolio</h2>
            <div className="portfolio-grid">
              {artist.portfolio_items.map((item, index) => (
                <div key={index} className="portfolio-item">
                  {item.type === 'image' ? (
                    <img src={item.url} alt={item.title || 'Portfolio item'} />
                  ) : item.type === 'video' ? (
                    <div className="video-container">
                      <iframe 
                        src={item.url} 
                        title={item.title || 'Video portfolio item'} 
                        frameBorder="0" 
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                        allowFullScreen
                      ></iframe>
                    </div>
                  ) : (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="portfolio-link">
                      {item.title || 'View work'}
                    </a>
                  )}
                  {item.title && <p className="portfolio-title">{item.title}</p>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArtistProfile;
